<template>
    <v-app v-cloak id="inspire">
        <loading-spinner></loading-spinner>

        <v-navigation-drawer v-if="$vuetify.display.lgAndUp && isLoggedIn" app :rail="isMiniVariant">
            <navigation :is-mini-variant="isMiniVariant" @mini-variant-changed="miniVariantChanged" />
        </v-navigation-drawer>

        <v-navigation-drawer v-else-if="isLoggedIn" v-model="drawer" app>
            <navigation />
        </v-navigation-drawer>

        <v-app-bar app dark color="primary">
            <div v-if="!isLoggedIn" class="topLeftIcon">
                <v-img src="@/assets/vetdata-Logo.jpg" height="64"></v-img>
            </div>
            <v-app-bar-nav-icon v-else-if="$vuetify.display.mdAndDown" @click="isMiniVariant = !isMiniVariant" @click.stop="drawer = !drawer" />
            <v-toolbar-title>
                <h3>{{ $t(subTitle) }}</h3>
            </v-toolbar-title>

            <auto-logout v-if="isLoggedIn"></auto-logout>

            <div v-if="isLoggedIn" class="pr-4 pt-1" @click="openMailToFeedback({ lang: language, senderInfo: benutzerInfo, screen: footerTitle })">
                <u class="feedbackEmailLink">{{ $t('Feedback E-Mail') }}</u>
            </div>
            <div class="pr-3" style="width: 70px">
                <v-select
                    id="languageSelect"
                    class="language-select"
                    :items="languages"
                    hide-details
                    dense
                    :model-value="language.toUpperCase()"
                    @update:model-value="changeLanguage"
                ></v-select>
            </div>
            <v-tooltip v-if="isLoggedIn" bottom color="secondary">
                <template #activator="{ props }">
                    <div v-bind="props">
                        <v-btn style="cursor: default" icon class="mr-3">
                            <v-icon x-large>mdi-account-cog</v-icon>
                        </v-btn>
                        <label v-if="!$vuetify.display.xs" class="mr-6">
                            <span v-if="benutzerInfo.tvsKundenNummer">
                                {{ $t('tvs.nr') }}:
                                <b>{{ benutzerInfo.tvsKundenNummer }}</b>
                            </span>
                            <span v-else-if="benutzerInfo.tvsLieferantenNummer">
                                {{ $t('lieferant.nr') }}:
                                <b>{{ benutzerInfo.tvsLieferantenNummer }}</b>
                            </span>
                            <span v-else>
                                <b>{{ benutzerInfo.anzeigeName }}</b>
                                <span v-if="benutzerInfo.isKontextSet"> ({{ benutzerInfo.teilnehmerName }} - {{ benutzerInfo.teilnehmerId }}) </span>
                            </span>
                        </label>
                    </div>
                </template>
                <div>
                    <span>
                        <label v-if="$vuetify.display.xs">
                            <span v-if="benutzerInfo.tvsKundenNummer">
                                {{ $t('tvs.nr') }}:
                                <b>{{ benutzerInfo.tvsKundenNummer }}</b>
                            </span>
                            <span v-else-if="benutzerInfo.tvsLieferantenNummer">
                                {{ $t('lieferant.nr') }}:
                                <b>{{ benutzerInfo.tvsLieferantenNummer }}</b>
                            </span>
                            <br />
                        </label>
                        <label>{{ benutzerInfo.anzeigeName }}</label>

                        <br />
                        <label v-if="benutzerInfo.rollen">{{ $t('benutzerrollen.' + benutzerInfo.rollen.toString().toLowerCase()).toString() }}</label>
                    </span>
                </div>
            </v-tooltip>
            <v-btn v-if="isLoggedIn && permissions.length > 0" icon link :to="{ name: 'Login' }" @click="logout">
                <v-icon>mdi-logout</v-icon>
            </v-btn>
            <header-notification></header-notification>
        </v-app-bar>
        <v-main class="accent">
            <agb-confirm-dialog @openagblink="openAgbInNewTab" />
            <startup-message-dialog />
            <v-row v-if="(isLoggedIn && !hasAccess('Navigation.GuiAccess') && permissions.length > 0) || accessDenied">
                <v-col class="pl-9">
                    <v-row>
                        <h2>Keinen Zugriff</h2>
                    </v-row>
                    <v-row>
                        <p>Sie besitzen nicht die nötigen Rechte um auf diese Seite zuzugreifen.</p>
                    </v-row>
                    <v-row>
                        <v-btn small color="primary" @click="logout">{{ $t('login.button.logout') }}</v-btn>
                    </v-row>
                </v-col>
            </v-row>
            <v-row v-else>
                <v-col>
                    <router-view />
                </v-col>
            </v-row>
        </v-main>
        <v-footer color="grey" app>
            <span class="text-white">
                <div class="d-inline mr-10">[{{ footerTitle }}]</div>
                <div class="d-inline mr-10">{{ timestamp }}</div>
                <div class="d-inline mr-10">© {{ currentYear }}</div>
                <!-- Version und System -->
                <div class="d-inline mr-10">{{ AppTitle }} V{{ AppVersion }} {{ AppEnviroment }}</div>
            </span>
        </v-footer>
    </v-app>
</template>

<script lang="ts">
import { Component, Watch, mixins } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';

import { DateMixin } from '@/mixins/DateMixin';

import { NAME as NAME_GENERAL } from '@/store/modules/general';
const generalModul = namespace(NAME_GENERAL);
import { TITLE, FOOTERTITLE } from '@/store/modules/general/getters';
import { FEEDBACK_MAILTO, LOAD_FRONTENDCONFIGS } from './store/modules/general/actions';

import { NAME as NAME_RESOURCE } from '@/store/modules/resource';
const resourceModul = namespace(NAME_RESOURCE);
import { LOAD_LANGUAGE } from '@/store/modules/resource/actions';
import { GET_LANGUAGE } from '@/store/modules/resource/getters';

import { NAME as NAME_VALIDATION } from '@/store/modules/validation';
const validationModul = namespace(NAME_VALIDATION);
import { LOAD_ALLOWED_FILETYPES, LOAD_VALIDATION } from '@/store/modules/validation/actions';

import { NAME as NAME_SECURITY } from '@/store/modules/security';
const securityModul = namespace(NAME_SECURITY);
import { LOGOUT, GET_BENUTZERINFO, GET_PERMISSIONS, FETCH_STARTUP_MESSAGE } from '@/store/modules/security/actions';
import { ISLOGGEDIN, BENUTZERINFO, HASACCESS, PERMISSIONS, ACCESSDENIED } from '@/store/modules/security/getters';

import navigation from './components/Navigation.vue';
import loadingSpinner from './components/LoadingSpinner.vue';
import headerNotification from './components/HeaderNotification.vue';
import AgbConfirmDialog from './components/S110_AGBConfirmDialog.vue';
import StartupMessageDialog from './components/StartupMessageDialog.vue';
import autoLogout from './components/AutoLogout.vue';
import { BenutzerInfoApiDto } from '@/api-schema';

@Component({
    components: {
        navigation,
        loadingSpinner,
        headerNotification,
        AgbConfirmDialog,
        StartupMessageDialog,
        autoLogout
    }
})
export default class App extends mixins(DateMixin) {
    isMiniVariant = false;
    languages: Array<string> = ['DE', 'FR'];
    drawer: string | null = null;
    timestamp = '';
    currentYear = '';

    @generalModul.Getter(TITLE) subTitle!: Function;
    @generalModul.Getter(FOOTERTITLE) footerTitle!: Function;
    @generalModul.Action(LOAD_FRONTENDCONFIGS) loadFrontendConfigs!: Function;
    @generalModul.Action(FEEDBACK_MAILTO) openMailToFeedback!: Function;

    @resourceModul.Action(LOAD_LANGUAGE) loadLanguage!: Function;
    @resourceModul.Getter(GET_LANGUAGE) language!: string;

    @validationModul.Action(LOAD_VALIDATION) loadValidation!: Function;
    @validationModul.Action(LOAD_ALLOWED_FILETYPES) loadAlowedFileTypes!: Function;

    @securityModul.Getter(ISLOGGEDIN) isLoggedIn!: boolean;
    @securityModul.Getter(BENUTZERINFO) benutzerInfo!: BenutzerInfoApiDto;
    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Getter(PERMISSIONS) permissions: Array<string>;
    @securityModul.Getter(ACCESSDENIED) accessDenied!: boolean;
    @securityModul.Action(LOGOUT) logout!: Function;
    @securityModul.Action(GET_BENUTZERINFO) getBenutzerInfo!: Function;
    @securityModul.Action(GET_PERMISSIONS) getPermissions!: Function;
    @securityModul.Action(FETCH_STARTUP_MESSAGE) fetchStartupMessage!: Function;

    async created() {
        await this.loadFrontendConfigs();

        this.loadValidation();
        this.loadAlowedFileTypes();
        setInterval(this.getNow, 1000);
        console.log('title:', config.VUE_APP_TITLE);
        console.log('enviroment:', config.VUE_APP_ENVIROMENT);
        console.log('version:', process.env.VUE_APP_VERSION);
        // wenn konfiguriert, startupMessage einblenden
        if (this.isLoggedIn) {
            await this.fetchStartupMessage();
        }
    }

    @Watch('isLoggedIn')
    private async onIsLoggedInChanged(newVal: boolean) {
        if (newVal) {
            await this.fetchStartupMessage();
        }
    }

    private changeLanguage(l: string) {
        console.log('Sprache wird geändert auf: ' + l);
        this.loadLanguage(l.toLowerCase());
    }

    private getNow() {
        const today = new Date();
        this.currentYear = today.getFullYear().toString();
        const date = this.toSwissDateString(today);
        let timebinder = ':';
        if (today.getMinutes() < 10) {
            timebinder += '0';
        }
        const time = today.getHours() + timebinder + today.getMinutes();
        const dateTime = date + ' ' + time;
        this.timestamp = dateTime;
    }

    private get AppVersion() {
        return process.env.VUE_APP_VERSION;
    }
    private get AppEnviroment() {
        return config.VUE_APP_ENVIROMENT;
    }
    private get AppTitle() {
        return config.VUE_APP_TITLE;
    }

    private miniVariantChanged(isMiniVariant: boolean) {
        this.isMiniVariant = isMiniVariant;
    }

    private openAgbInNewTab() {
        if (this.language.toLowerCase() == 'de') {
            window.open(config.VUE_APP_AGB_LINK_GERMAN);
        } else if (this.language.toLowerCase() == 'fr') {
            window.open(config.VUE_APP_AGB_LINK_FRENCH);
        }
    }

    @Watch('subTitle')
    private subTitleChanged(newVal: string) {
        document.title = config.VUE_APP_TITLE + ' - ' + newVal;
    }
}
</script>
<style>
[v-cloak] {
    display: none;
}
#v-app {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
}

#v-nav {
    padding: 30px;
}

.v-toolbar__content {
    overflow: visible !important;
}

#v-nav a {
    font-weight: bold;
    color: #2c3e50;
}

#v-nav a.router-link-exact-active {
    color: #42b983;
}
.headerNotification {
    position: absolute !important;
    margin-top: 129px;
    width: 100%;
    z-index: 1;
}
.modalHeaderNotification {
    width: 100%;
    z-index: 1;
}
.topLeftIcon {
    height: 64px;
    width: 255px;
    margin-right: 25px;
}
.v-img__img {
    object-fit: fill !important;
}

.feedbackEmailLink {
    font-size: 17px;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
}
.modalbutton:focus {
    border: 3px solid black;
}
.filterLine {
    border-bottom: 1px solid gray;
}
.v-data-table td.text-start {
    overflow-wrap: break-word;
    max-width: 400px;
}
.language-select {
    margin-bottom: 12px;
}
.table-header-item-count {
    font-size: 16px;
}

.fit-content {
    min-width: fit-content !important;
    max-width: 100% !important;
    flex: none !important;
}

.equal-btn {
    flex: 1; /* make both take equal available width */
    display: flex; /* ensure child stretches */
}

.equal-btn > * {
    flex: 1; /* child fills wrapper */
}

/* Das Menu eines v-select innerhalb eines Dialoges soll komplett sichtbar sein */
#focusLockDialogForm .v-overlay__content {
    min-width: max-content !important;
}
</style>
